/**
 * Module 50 (WP-50.27) — Rainbow Gates: activate, every gate accepts every
 * colour, and it stays that way until one block passes through a gate under its
 * own power.
 *
 * ## What this replaces, and why
 *
 * Until WP-50.27 this file removed the tapped block from the array — mechanically
 * identical to the Hammer, with `fx: 'exit'` flying it to the nearest gate for
 * fiction. The comment here used to argue that was correct. It was not, for two
 * reasons that only showed up in a real session:
 *
 *  - **The flight was a lie.** A block walled in behind three others sailed
 *    across the board, through them, to a gate it could never have reached.
 *    Reported by the user in exactly those terms.
 *  - **It made one of the three power-ups dead content.** Same effect as the
 *    Hammer means whichever is cheaper is the only one anybody buys.
 *
 * ## The new effect
 *
 * `activation: 'immediate'` — there is no target to pick. The board switches
 * into a mode (`rainbowSinceMove`), and the exit predicate every mover already
 * shares reads it through `ExitOptions.rainbow`. The block leaves by being
 * SLID to a gate, so the power-up spends move budget, which is what makes it a
 * genuinely different item from the Hammer rather than a cheaper one.
 *
 * ## What it does NOT relax
 *
 * Colour, and only colour. `exitsThroughGate` still requires the facing edge to
 * be flush against the wall and every facing lane to be inside the opening, so
 * an unreachable block stays put and an over-wide block does not fit. The
 * WP-35.5 `ExitOptions.rainbow` hook — added for this reading, unused since,
 * with a comment asking a later WP to decide its fate — is finally the thing
 * carrying the effect. That comment is resolved rather than re-deferred.
 *
 * `targets`/`apply` are the seam's other shape and are unreachable for this id:
 * the plugin checks `activation` before it enters targeting. They return
 * empty/null rather than throwing, so a future caller that reaches them gets a
 * board that did not change instead of a crash mid-level.
 */
import { BlockJamAudioId } from '../../constants/audio-ids';
import { activateRainbow, endRainbow, isRainbowActive } from '../../model/rainbow';
export const RAINBOW_GATES_ID = 'rainbowGates';
export const rainbowGatesEffect = {
    /** A board mode, not a removal — see the header. */
    activation: 'immediate',
    /** Module 90 — paid on the exit that uses it; cancellable until then. */
    chargesWhenSpent: true,
    isOpen: isRainbowActive,
    close: endRainbow,
    activate(state) {
        // Already open: opening it again would reset `rainbowSinceMove` and
        // under-report `moves_open`. A tap on the button while it is open cancels
        // it (Module 90) and never reaches here, so this is the second lock.
        if (isRainbowActive(state))
            return null;
        // No gates and nothing could ever leave through one, so the mode is inert.
        if (state.puzzle.exits.length === 0)
            return null;
        if (state.blocks.length === 0)
            return null;
        return activateRainbow(state);
    },
    /** Unreachable for an `'immediate'` effect — see the header. */
    targets() {
        return [];
    },
    /** Unreachable for an `'immediate'` effect — see the header. */
    apply() {
        return null;
    },
    /**
     * The activation beat: the gates turning rainbow IS the effect now, where it
     * used to be a paint job over a removal that happened elsewhere. Same cue, one
     * moment earlier in the flow — no new asset, so nothing here needs the Module
     * 39 assignment pipeline.
     */
    impactAudioId: BlockJamAudioId.POWERUP_RAINBOW,
};
